import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Container,
  Autocomplete,
  TextField,
  MenuItem,
  Button,
  Card as MuiCard,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import rentbanner from "@assets/images/1-Real-Estate-Banner.jpg";
import Card from "../Buy/card";
import { apiService } from "@services/api";
import { useRentFilter } from "@context/rentFilter.jsx";
import ContactSection from "../Home/contactSection";
import {
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaRegClock,
  FaHandshake,
  FaShieldAlt,
  FaUserCheck,
} from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import SEO from "../../../utils/SeoComponent";

// Styled Components
const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: "relative",
  overflow: "hidden",
}));

const PageSection1 = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(2),
  },
}));

const HeroSection = styled(PageSection)(({ theme }) => ({
  backgroundColor: "#f8f9fa",
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${rentbanner})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  color: "white",
  padding: theme.spacing(15, 0),
  textAlign: "center",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "150px",
    background: "linear-gradient(to top, #fff, transparent)",
  },
}));

const SearchBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: "10px",
  padding: theme.spacing(5),
  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
  position: "relative",
  zIndex: 2,
  marginTop: "-100px",
  [theme.breakpoints.down("md")]: {
    marginTop: "-50px",
    padding: theme.spacing(3),
  },
}));

const SearchButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#C10E14",
  color: "#fff",
  padding: theme.spacing(1.5, 4),
  borderRadius: "50px",
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.15)",
  "&:hover": {
    backgroundColor: "#A30B11",
  },
}));

const ClearButton = styled(Button)(({ theme }) => ({
  backgroundColor: "transparent",
  color: "black",
  border: "2px solid black",
  padding: theme.spacing(1.5, 4),
  borderRadius: "50px",
  fontWeight: 600,
  textTransform: "none",
  marginLeft: theme.spacing(2),
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  marginBottom: theme.spacing(4),
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(2),
  backgroundColor: "#f8f9fc",
  borderRadius: "50%",
  padding: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.5rem",
  color: "#C10E14",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  gap: theme.spacing(2),
  margin: theme.spacing(8, 0),
}));

const PaginationButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1, 3),
  borderRadius: "50px",
  fontWeight: 600,
  boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
}));

const NoResultsCard = styled(MuiCard)(({ theme }) => ({
  padding: theme.spacing(6),
  textAlign: "center",
  borderRadius: "15px",
  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
  marginTop: theme.spacing(6),
  marginBottom: theme.spacing(6),
  backgroundColor: "#f8f9fc",
}));

const RentPage = () => {
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [bedrooms, setBedrooms] = useState([]);
  const [prices, setPrices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 6;
  const [searchParams] = useSearchParams();
  let searchQuery = searchParams.get("query");

  const {
    selectedLocation,
    setSelectedLocation,
    selectedRoom,
    setSelectedRoom,
    selectedPrice,
    setSelectedPrice,
    filteredUsers,
    setFilteredUsers,
    isFilterApplied,
    setIsFilterApplied,
  } = useRentFilter();

  // Calculate pagination indices based on the current data set (filtered or unfiltered)
  const currentDataset = isFilterApplied ? filteredUsers : users;
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = currentDataset.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(currentDataset.length / usersPerPage);

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
      const element = document.getElementById("rental-listings");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
      const element = document.getElementById("rental-listings");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  // Reset to first page whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [isFilterApplied, filteredUsers]);

  const features = [
    {
      icon: <FaHandshake />,
      title: "Hassle-free Rentals",
      description:
        "Our streamlined process ensures a smooth rental experience from search to signing the lease.",
    },
    {
      icon: <FaMapMarkerAlt />,
      title: "Prime Locations",
      description:
        "Access to exclusive properties in Dubai's most desirable neighborhoods and communities.",
    },
    {
      icon: <FaMoneyBillWave />,
      title: "Competitive Pricing",
      description:
        "We offer transparent pricing with no hidden fees, ensuring you get the best value.",
    },
    {
      icon: <FaShieldAlt />,
      title: "Secure Transactions",
      description:
        "All rental agreements are legally verified and compliant with Dubai's real estate regulations.",
    },
    {
      icon: <FaUserCheck />,
      title: "Verified Listings",
      description:
        "Every property is personally inspected and verified by our professional team.",
    },
    {
      icon: <FaRegClock />,
      title: "Quick Processing",
      description:
        "Efficient application processing and move-in coordination to save you time.",
    },
  ];

  const handleClearFilters = () => {
    setSelectedLocation("");
    setSelectedRoom("");
    setSelectedPrice("");
    setFilteredUsers([]);
    setIsFilterApplied(false);
    setCurrentPage(1);
  };

  // Helper function to check if any filters are applied
  const hasFiltersApplied = () => {
    return selectedLocation || selectedRoom || selectedPrice;
  };

  const handleSearch = async () => {
    // Check if any filters are actually selected

    if (!hasFiltersApplied()) {
      console.log("No filters selected");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("location", selectedLocation);
      formData.append("room", selectedRoom);
      formData.append("price", selectedPrice);

      const response = await apiService.filterRentProperties(formData);
      const filteredData = response.data.data;

      setFilteredUsers(filteredData);
      setIsFilterApplied(true);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
      // Handle error case - don't set isFilterApplied to true
      setFilteredUsers([]);
      setIsFilterApplied(false);
    }
  };

  // Separate function to handle search query from URL
  const handleSearchQuery = async (query) => {
    if (!query) return;

    try {
      const formData = new FormData();
      formData.append("title", query); // Search by title/query instead of filters

      const response = await apiService.filterRentProperties(formData);
      const filteredData = response.data.data;

      setFilteredUsers(filteredData);
      setIsFilterApplied(true);
      setCurrentPage(1);
      searchQuery = null;
    } catch (error) {
      console.error("Error fetching search results:", error);
      setFilteredUsers([]);
      setIsFilterApplied(false);
    }
  };

  useEffect(() => {
    apiService
      .getRentProperties()
      .then((response) => {
        const data = response.data.data;

        const locationList = data
          .map((item) => item.location)
          .filter(
            (value, index, self) => value && self.indexOf(value) === index
          )
          .map((loc) => ({ label: loc }));

        const bedroomList = data
          .map((item) => item.room)
          .filter((v, i, a) => v && a.indexOf(v) === i)
          .map((room) => ({ label: `${room} Bedroom${room > 1 ? "s" : ""}` }));

        const priceList = data
          .map((item) => item.price)
          .filter((v, i, a) => v && a.indexOf(v) === i)
          .map((price) => ({ label: `AED ${price}` }));

        setLocations(locationList);
        setBedrooms(bedroomList);
        setPrices(priceList);
        setUsers(response.data.data);
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
      });
  }, []);

  // Handle search query from URL parameters
  useEffect(() => {
    if (searchQuery) {
      handleSearchQuery(searchQuery);
    }
  }, [searchQuery]);

  return (
    <>
      <SEO slug={"rent"} />

      <Box className="rent-page">
        {/* Hero Section */}
        <HeroSection className="hero-section">
          <Container
            maxWidth="md"
            sx={{
              mt: { xs: 6, md: 14 },
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                fontWeight={700}
              >
                Properties for Rent in Dubai
              </Typography>
              <Typography
                variant="h5"
                component="p"
                sx={{ mb: 4, opacity: 0.9 }}
              >
                Find your perfect home with our exclusive rental listings
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  const element = document.getElementById("rental-listings");
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                sx={{
                  borderRadius: "50px",
                  py: 1.5,
                  px: 4,
                  fontSize: "1rem",
                  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.25)",
                  backgroundColor: "#C10E14",
                  "&:hover": {
                    backgroundColor: "#A30B11",
                  },
                }}
              >
                Browse Properties
              </Button>
            </motion.div>
          </Container>
        </HeroSection>

        {/* Search Section */}
        <Container>
          <SearchBox className="search-section">
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <Autocomplete
                  disablePortal
                  options={locations}
                  getOptionLabel={(option) => option.label}
                  value={selectedLocation ? { label: selectedLocation } : null}
                  onChange={(event, newValue) =>
                    setSelectedLocation(newValue?.label || "")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Location"
                      fullWidth
                      sx={{
                        "& .MuiInputLabel-root": {
                          color: "black",
                          "&.Mui-focused": {
                            color: "#C10E14",
                          },
                        },
                        "& .MuiOutlinedInput-root": {
                          color: "black",
                          "& fieldset": {
                            borderColor: "black",
                          },
                          "&:hover fieldset": {
                            borderColor: "black",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#C10E14",
                          },
                        },
                        "& .MuiSvgIcon-root": {
                          color: "black",
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label="Bedroom"
                  value={selectedRoom}
                  onChange={(e) => setSelectedRoom(e.target.value)}
                  fullWidth
                  sx={{
                    "& .MuiInputLabel-root": {
                      color: "black",
                      "&.Mui-focused": {
                        color: "#C10E14",
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      color: "black",
                      "& fieldset": {
                        borderColor: "black",
                      },
                      "&:hover fieldset": {
                        borderColor: "black",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#C10E14",
                      },
                    },
                    "& .MuiSvgIcon-root": {
                      color: "black",
                    },
                  }}
                >
                  {bedrooms.map((room, index) => (
                    <MenuItem key={index} value={room.label}>
                      {room.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label="Price"
                  value={selectedPrice}
                  onChange={(e) => setSelectedPrice(e.target.value)}
                  fullWidth
                  sx={{
                    "& .MuiInputLabel-root": {
                      color: "black",
                      "&.Mui-focused": {
                        color: "#C10E14",
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      color: "black",
                      "& fieldset": {
                        borderColor: "black",
                      },
                      "&:hover fieldset": {
                        borderColor: "black",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#C10E14",
                      },
                    },
                    "& .MuiSvgIcon-root": {
                      color: "black",
                    },
                  }}
                >
                  {prices.map((price, index) => (
                    <MenuItem key={index} value={price.label}>
                      {price.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: "flex" }}>
                  <SearchButton
                    fullWidth
                    onClick={handleSearch}
                    disabled={!hasFiltersApplied()}
                  >
                    Search
                  </SearchButton>
                  {isFilterApplied && (
                    <ClearButton onClick={handleClearFilters}>
                      Clear
                    </ClearButton>
                  )}
                </Box>
              </Grid>
            </Grid>
          </SearchBox>
        </Container>

        {/* Features Section */}
        <PageSection1 className="features-section" sx={{ bgcolor: "#f8f9fc" }}>
          <Container>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h4"
                component="h2"
                fontWeight={700}
                sx={{ mb: 2 }}
              >
                Why Rent with DESCO?
              </Typography>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ maxWidth: "700px", mx: "auto" }}
              >
                We make finding and renting your dream property in Dubai simple,
                secure, and stress-free
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <FeatureItem>
                      <IconWrapper>{feature.icon}</IconWrapper>
                      <Box>
                        <Typography
                          variant="h6"
                          component="h3"
                          fontWeight={600}
                          gutterBottom
                        >
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {feature.description}
                        </Typography>
                      </Box>
                    </FeatureItem>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </PageSection1>

        {/* Properties Listing Section */}
        <PageSection1 id="rental-listings" className="properties-section">
          <Container>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h4"
                component="h2"
                fontWeight={700}
                sx={{ mb: 2 }}
              >
                Available Rental Properties
              </Typography>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ maxWidth: "700px", mx: "auto" }}
              >
                {isFilterApplied
                  ? `Showing ${currentUsers.length} of ${filteredUsers.length} filtered properties`
                  : `Browse our exclusive selection of rental properties across Dubai`}
              </Typography>
            </Box>

            {isFilterApplied ? (
              filteredUsers && filteredUsers.length > 0 ? (
                <>
                  <Card data={currentUsers} pageType={"rent"} />
                  <PaginationContainer>
                    <PaginationButton
                      variant="outlined"
                      onClick={handlePrevious}
                      disabled={currentPage === 1}
                      sx={{
                        backgroundColor:
                          currentPage === 1 ? "#f5f5f5" : "#C10E14",
                        color: currentPage === 1 ? "#888" : "#fff",
                        "&:hover": {
                          backgroundColor:
                            currentPage === 1 ? "#f5f5f5" : "#A30B11",
                        },
                      }}
                    >
                      Previous
                    </PaginationButton>
                    <Typography
                      variant="subtitle1"
                      sx={{ alignSelf: "center" }}
                    >
                      Page {currentPage} of {totalPages}
                    </Typography>
                    <PaginationButton
                      variant="contained"
                      onClick={handleNext}
                      disabled={currentPage === totalPages}
                      sx={{
                        backgroundColor:
                          currentPage === totalPages ? "#f5f5f5" : "#C10E14",
                        color: currentPage === totalPages ? "#888" : "#fff",
                        "&:hover": {
                          backgroundColor:
                            currentPage === totalPages ? "#f5f5f5" : "#A30B11",
                        },
                      }}
                    >
                      Next
                    </PaginationButton>
                  </PaginationContainer>
                </>
              ) : (
                <Container maxWidth="md">
                  <NoResultsCard>
                    <Typography variant="h5" component="h3" gutterBottom>
                      😕 No Results Found
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      paragraph
                    >
                      We couldn't find any properties matching your selected
                      filters.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleClearFilters}
                      sx={{
                        borderRadius: "50px",
                        py: 1,
                        px: 3,
                        mt: 2,
                        backgroundColor: "#C10E14",
                        "&:hover": {
                          backgroundColor: "#A30B11",
                        },
                      }}
                    >
                      Clear All Filters
                    </Button>
                  </NoResultsCard>
                </Container>
              )
            ) : (
              <>
                <Card data={currentUsers} pageType={"rent"} />
                {users.length > 0 && (
                  <PaginationContainer>
                    <PaginationButton
                      variant="outlined"
                      onClick={handlePrevious}
                      disabled={currentPage === 1}
                      sx={{
                        backgroundColor:
                          currentPage === 1 ? "#f5f5f5" : "#C10E14",
                        color: currentPage === 1 ? "#888" : "#fff",
                        "&:hover": {
                          backgroundColor:
                            currentPage === 1 ? "#f5f5f5" : "#A30B11",
                        },
                      }}
                    >
                      Previous
                    </PaginationButton>
                    <Typography
                      variant="subtitle1"
                      sx={{ alignSelf: "center" }}
                    >
                      Page {currentPage} of {totalPages}
                    </Typography>
                    <PaginationButton
                      variant="contained"
                      onClick={handleNext}
                      disabled={currentPage === totalPages}
                      sx={{
                        backgroundColor:
                          currentPage === totalPages ? "#f5f5f5" : "#C10E14",
                        color: currentPage === totalPages ? "#888" : "#fff",
                        "&:hover": {
                          backgroundColor:
                            currentPage === totalPages ? "#f5f5f5" : "#A30B11",
                        },
                      }}
                    >
                      Next
                    </PaginationButton>
                  </PaginationContainer>
                )}
              </>
            )}
          </Container>
        </PageSection1>

        {/* Contact Section */}
        <Box
          sx={{
            padding: "80px 0px",
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${rentbanner})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <ContactSection />
        </Box>
      </Box>
    </>
  );
};

export default RentPage;
